/**
 * The backend tells the model to end a reply that asks a question with its suggested answers as a numbered
 * list, because the conversation memory keeps only the text. The widget shows those answers as chips, so the
 * list is dropped from the text, but only when it matches the options exactly. Anything else stays visible.
 */

import { AttributeMode, ClarifyingQuestion, Preset, TextFormat, TimeRangeAnswer } from './api/assistant.service';

const LIST_ITEM = /^\s*\d+[.)]\s+(.+?)\s*$/;

/** The reply text without a trailing numbered list that repeats the question's options in order. */
export function withoutOptionList(text: string, question: ClarifyingQuestion | undefined): string {
    const options = question?.options ?? [];
    if (options.length === 0) {
        return text;
    }
    const lines = text.trimEnd().split('\n');
    const start = lines.length - options.length;
    if (start < 0) {
        return text;
    }
    const matches = lines.slice(start).every((line, index) => {
        const item = LIST_ITEM.exec(line);
        return !!item && normalize(item[1]) === normalize(options[index]);
    });
    if (!matches || (start > 0 && LIST_ITEM.test(lines[start - 1]))) {
        return text;
    }
    return lines.slice(0, start).join('\n').trimEnd();
}

const DURATION = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

/** The length of an ISO-8601 duration in days, hours, minutes and seconds, in milliseconds; null when it is not one. */
export function durationMs(iso: string): number | null {
    const match = DURATION.exec(iso);
    if (!match || iso === 'P' || iso.endsWith('T')) {
        return null;
    }
    const [days, hours, minutes, seconds] = match.slice(1).map((part) => Number(part ?? 0));
    return (((days * 24 + hours) * 60 + minutes) * 60 + seconds) * 1000;
}

/** The answer for a preset picked now: the range of its length that ends now. Null when the duration is unreadable. */
export function presetAnswer(preset: Preset, now: Date = new Date()): TimeRangeAnswer | null {
    const length = durationMs(preset.duration);
    if (!length) {
        return null;
    }
    return { type: 'TIME_RANGE', from: new Date(now.getTime() - length).toISOString(), to: now.toISOString() };
}

/**
 * The answer for a custom range from two `datetime-local` values, read in the browser's zone. Null unless from is
 * before to.
 */
export function customRangeAnswer(from: string, to: string): TimeRangeAnswer | null {
    const start = new Date(from);
    const end = new Date(to);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start >= end) {
        return null;
    }
    return { type: 'TIME_RANGE', from: start.toISOString(), to: end.toISOString() };
}

/** A `datetime-local` value for a moment, in the browser's zone. */
export function toDateTimeLocal(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export type DurationUnit = 'M' | 'H' | 'D';

/** An ISO-8601 duration for an amount of minutes, hours or days; null unless the amount is a positive whole number. */
export function toIsoDuration(amount: number | null, unit: DurationUnit): string | null {
    if (amount === null || !Number.isInteger(amount) || amount <= 0) {
        return null;
    }
    return unit === 'D' ? `P${amount}D` : `PT${amount}${unit}`;
}

/** A length as people read it: "1 hour", "4 hours", "7 days". */
export function lengthLabel(amount: number, unit: DurationUnit): string {
    const word = { M: 'minute', H: 'hour', D: 'day' }[unit];
    return `${amount} ${word}${amount === 1 ? '' : 's'}`;
}

const LABEL = '[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?';
const HOSTNAME = new RegExp(`^(?:\\*\\.)?${LABEL}(?:\\.${LABEL})*$`);
const DOMAIN = new RegExp(`^${LABEL}(?:\\.${LABEL})+$`);
const IPV4 = /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
const PORT = /^[1-9]\d{0,4}$/;
const PREFIX = /^(?:0|[1-9]\d{0,2})$/;
const EMAIL = new RegExp(`^[A-Za-z0-9._%+-]{1,64}@${LABEL}(?:\\.${LABEL})+$`);

/** The same checks the server applies to a TEXT answer, so the user hears about a mistake before sending it. */
export function isValidText(format: TextFormat, raw: string): boolean {
    const value = raw.trim();
    if (!value || value.length > 253) {
        return false;
    }
    switch (format) {
        case 'HOSTNAME':
            return HOSTNAME.test(value);
        case 'IP':
            return IPV4.test(value) || isIpv6(value);
        case 'CIDR': {
            const [address, prefix, ...rest] = value.split('/');
            if (rest.length || prefix === undefined || !PREFIX.test(prefix)) {
                return false;
            }
            return IPV4.test(address) ? Number(prefix) <= 32 : isIpv6(address) && Number(prefix) <= 128;
        }
        case 'HOST_PORT': {
            if (value.startsWith('[')) {
                const close = value.indexOf(']:');
                return close > 0 && isIpv6(value.slice(1, close)) && isPort(value.slice(close + 2));
            }
            const parts = value.split(':');
            return parts.length === 2 && (HOSTNAME.test(parts[0]) || IPV4.test(parts[0])) && isPort(parts[1]);
        }
        case 'PORT':
            return isPort(value);
        case 'DOMAIN_NAME':
            return DOMAIN.test(value);
        case 'EMAIL':
            return EMAIL.test(value);
    }
}

/** The URL parser accepts exactly the IPv6 literals; a zone id is refused, as the server does. */
function isIpv6(value: string): boolean {
    if (!value.includes(':') || value.includes('%') || !/^[0-9A-Fa-f:.]+$/.test(value)) {
        return false;
    }
    try {
        new URL(`http://[${value}]/`);
        return true;
    } catch {
        return false;
    }
}

function isPort(value: string): boolean {
    return PORT.test(value) && Number(value) <= 65535;
}

const ATTRIBUTE = /^[\p{L}\p{N}][\p{L}\p{N}._:/ -]{0,99}$/u;
const ROLE = /^[#@][\p{L}\p{N}][\p{L}\p{N}._:/ -]{0,99}$/u;

/** The same check the server applies to each value of an ATTRIBUTES answer. */
export function isValidAttribute(mode: AttributeMode, raw: string): boolean {
    return (mode === 'ROLES' ? ROLE : ATTRIBUTE).test(raw.trim());
}

/** Compare as text, ignoring markdown emphasis, quotes, case, spacing and a trailing full stop. */
function normalize(value: string): string {
    return value
        .replace(/[*_`"“”]/g, '')
        .replace(/\s+/g, ' ')
        .replace(/\.$/, '')
        .trim()
        .toLowerCase();
}
